import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Checkbox,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { Card } from 'react-bootstrap';
import request from 'tools/request';
import { Toast } from 'components/shared/Toaster';
import ConfirmationDialog from 'components/ConfirmationDialog';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { featureFlagLabel, featureFlagCategory, compareFeatureFlags, FEATURE_FLAG_CATEGORIES } from 'constants/featureFlags';

const flagLabel = featureFlagLabel;

const pageLimitOptions = [
    { label: 'Showing 5 Records', value: 5 },
    { label: 'Showing 10 Records', value: 10 },
    { label: 'Showing 20 Records', value: 20 },
    { label: 'Showing 40 Records', value: 40 },
    { label: 'Showing 50 Records', value: 50 },
];

const FeatureFlagsTab = () => {
    const [availableFlags, setAvailableFlags] = useState([]);
    const [selectedFlag, setSelectedFlag] = useState('');
    const [clientFlags, setClientFlags] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [loadingFlags, setLoadingFlags] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    // Fetch available feature flags
    const fetchAvailableFlags = useCallback(() => {
        setLoadingFlags(true);
        request('clients/feature-flags/available', 'GET')
            .then((res) => {
                setAvailableFlags(res || []);
            })
            .finally(() => {
                setLoadingFlags(false);
            });
    }, []);

    useEffect(() => {
        fetchAvailableFlags();
    }, [fetchAvailableFlags]);

    // Fetch clients for the selected flag
    // A refresh after an action keeps the page and search, so a toggle doesn't jump back to page 1.
    const fetchClientFlags = useCallback((keepPosition = false) => {
        if (!selectedFlag) return;
        if (!keepPosition) {
            setLoadingClients(true);
            setPage(0);
        }
        setSelectedClientIds([]);
        request('clients/feature-flags', 'GET', null, false, {}, { flag: selectedFlag })
            .then((res) => {
                setClientFlags(res || []);
            })
            .finally(() => {
                setLoadingClients(false);
            });
    }, [selectedFlag]);

    useEffect(() => {
        fetchClientFlags();
    }, [fetchClientFlags]);

    // Search by client name or UUID
    const filteredClients = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return clientFlags;
        return clientFlags.filter((c) =>
            (c.clientName || '').toLowerCase().includes(term) || String(c.clientId).toLowerCase().includes(term)
        );
    }, [clientFlags, search]);

    // Pagination computed values
    const totalRecords = filteredClients.length;
    const totalPages = Math.ceil(totalRecords / limit);
    const paginatedData = useMemo(() => {
        const start = page * limit;
        return filteredClients.slice(start, start + limit);
    }, [filteredClients, page, limit]);

    // A refresh or a narrower search can leave the page past the end.
    useEffect(() => {
        if (page > 0 && page >= totalPages) setPage(Math.max(totalPages - 1, 0));
    }, [page, totalPages]);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPage(0);
        setSelectedClientIds([]);
    };

    // Handle individual row checkbox
    const handleRowSelect = (clientId) => {
        setSelectedClientIds((prev) =>
            prev.includes(clientId)
                ? prev.filter((id) => id !== clientId)
                : [...prev, clientId]
        );
    };

    // Select all selects every client matching the search, not just the visible page
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedClientIds(filteredClients.map((c) => c.clientId));
        } else {
            setSelectedClientIds([]);
        }
    };

    // Bulk action handler
    const handleBulkAction = (action, clientIds = null) => {
        setActionLoading(true);
        const body = {
            flag: selectedFlag,
            action,
            clientIds,
        };
        request('clients/feature-flags/bulk', 'POST', body)
            .then((res) => {
                Toast({ type: 'success', message: res?.message || 'Action completed successfully' });
                fetchClientFlags(true);
            })
            .finally(() => {
                setActionLoading(false);
            });
    };

    // Opens confirmation dialog before performing any action
    const confirmAction = (action, clientIds) => {
        const isEnabling = action === 'ENABLE';
        const isAll = clientIds === null;
        const count = clientIds?.length;

        let message;
        if (isAll) {
            message = `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} "${flagLabel(selectedFlag)}" for ALL active clients?`;
        } else if (count === 1) {
            const client = clientFlags.find(c => c.clientId === clientIds[0]);
            message = `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} "${flagLabel(selectedFlag)}" for "${client?.clientName || 'this client'}"?`;
        } else {
            message = `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} "${flagLabel(selectedFlag)}" for ${count} selected clients?`;
        }

        setConfirmDialog({
            open: true,
            title: `${isEnabling ? 'Enable' : 'Disable'} Feature Flag`,
            message,
            onConfirm: () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                handleBulkAction(action, clientIds);
            },
        });
    };

    // Pagination handlers
    const handlePageChange = (e, value) => {
        setPage(value - 1);
    };

    const handleLimitChange = (event) => {
        setLimit(event.target.value);
        setPage(0);
    };

    const allSelected =
        filteredClients.length > 0 && selectedClientIds.length === filteredClients.length;
    const someSelected =
        selectedClientIds.length > 0 && selectedClientIds.length < filteredClients.length;

    return (
        <Card className="p-4">
            <Typography variant="h6" gutterBottom>
                Feature Flags Management
            </Typography>

            {/* Every flag, grouped by category; picking one opens its client list below */}
            {loadingFlags ? (
                <Box mb={3}><CircularProgress size={20} /></Box>
            ) : (
                <Box mb={3}>
                    {FEATURE_FLAG_CATEGORIES
                        .map(category => [category, availableFlags.filter(flag => featureFlagCategory(flag) === category).sort(compareFeatureFlags)])
                        .filter(([, flags]) => flags.length > 0)
                        .map(([category, flags]) => (
                            <Box key={category} mb={2}>
                                <Typography variant="subtitle2" style={{ marginBottom: 8, color: 'var(--fg-secondary)' }}>
                                    {category}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gridGap={8}>
                                    {flags.map(flag => (
                                        <Button
                                            key={flag}
                                            size="small"
                                            color="primary"
                                            variant={selectedFlag === flag ? 'contained' : 'outlined'}
                                            aria-pressed={selectedFlag === flag}
                                            onClick={() => setSelectedFlag(flag)}
                                            style={{ textTransform: 'none' }}
                                        >
                                            {flagLabel(flag)}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                </Box>
            )}

            {selectedFlag && (
                <Typography variant="subtitle1" style={{ marginBottom: 8, fontWeight: 600 }}>
                    {flagLabel(selectedFlag)}
                </Typography>
            )}

            {selectedFlag && (
                <>
                    {/* Action Buttons */}
                    <Box mb={2} display="flex" flexWrap="wrap" gridGap={8}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={actionLoading || selectedClientIds.length === 0}
                            onClick={() => confirmAction('ENABLE', selectedClientIds)}
                        >
                            Enable for Selected
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={actionLoading || selectedClientIds.length === 0}
                            onClick={() => confirmAction('DISABLE', selectedClientIds)}
                        >
                            Disable for Selected
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={actionLoading}
                            onClick={() => confirmAction('ENABLE', null)}
                        >
                            Enable for All Clients
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={actionLoading}
                            onClick={() => confirmAction('DISABLE', null)}
                        >
                            Disable for All Clients
                        </Button>
                        {actionLoading && <CircularProgress size={24} />}
                    </Box>

                    {/* Client search */}
                    <Box mb={2}>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search clients by name or UUID"
                            value={search}
                            onChange={handleSearchChange}
                            style={{ minWidth: 360 }}
                            inputProps={{ 'aria-label': 'Search clients' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Client Table */}
                    {loadingClients ? (
                        <div className="text-center p-4">
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={someSelected}
                                                    checked={allSelected}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                            <TableCell><strong>UUID</strong></TableCell>
                                            <TableCell><strong>Client Name</strong></TableCell>
                                            <TableCell align="center"><strong>Enabled</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    {search.trim() ? 'No clients match your search.' : 'No clients found for this flag.'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedData.map((row) => (
                                                <TableRow key={row.clientId} hover>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedClientIds.includes(row.clientId)}
                                                            onChange={() => handleRowSelect(row.clientId)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{row.clientId}</TableCell>
                                                    <TableCell>{row.clientName}</TableCell>
                                                    <TableCell align="center">
                                                        <Switch
                                                            checked={row.enabled}
                                                            onChange={() => confirmAction(row.enabled ? 'DISABLE' : 'ENABLE', [row.clientId])}
                                                            color="primary"
                                                            disabled={actionLoading}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {filteredClients.length > 0 && (
                                <div className="global-table">
                                    <div className="pagination-container">
                                        <div className="pagination-limit">
                                            <div>
                                                <FormControl variant="outlined">
                                                    <Select
                                                        className="page-limit-dropdwon"
                                                        onChange={handleLimitChange}
                                                        value={limit}
                                                        variant="outlined"
                                                    >
                                                        {pageLimitOptions.map((ele, index) => (
                                                            <MenuItem key={index} value={ele.value}>
                                                                {ele.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                            <p className="total-records">{totalRecords} Total Records</p>
                                        </div>
                                        <div className="pagination-count">
                                            <Pagination
                                                count={totalPages}
                                                onChange={handlePageChange}
                                                color="primary"
                                                page={page + 1}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                loading={actionLoading}
            />
        </Card>
    );
};

export default FeatureFlagsTab;